import classNames from 'classnames'
import React from 'react'
import { Area, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { formatMonth, shortFormatNumber } from '../../../util'
import { EarningsEstimateType } from '../../earnings-common/types'
import { getMonthlyTicks } from '../../earnings-common/utils'
import {
  EarningsColor,
  PRICE_REVISIONS_CHART_HEIGHT,
  PRICE_REVISIONS_CHART_WIDTH,
  PRICE_REVISIONS_TICK_COUNT,
  PRICE_REVISIONS_YAXIS_WIDTH,
  REVISIONS_LEFT_TICK_STYLE,
  REVISIONS_RIGHT_TICK_STYLE,
  X_TICK_STYLE,
} from '../constants'
import { RevisionsChartData } from '../types'
import { getEarningsColor } from '../utils'
import { PriceRevisionTooltip } from './PriceRevisionTooltip'

const CHART_GRID_COMMON_PROPS = 'absolute border-t border-secondary left-11 right-10'

type Props = {
  activeIndex: number
  chartData: RevisionsChartData
  endingTimestamp: number
  estimateType: EarningsEstimateType
  isActive: boolean
  onSetActiveIndex: (value: number) => void
  onSetIsActive: (value: boolean) => void
  startingTimestamp: number
}

export function PriceRevisionsChart({
  activeIndex,
  chartData,
  endingTimestamp,
  estimateType,
  isActive,
  onSetActiveIndex,
  onSetIsActive,
  startingTimestamp,
}: Props) {
  const monthlyTicks = React.useMemo(
    () => getMonthlyTicks(startingTimestamp, endingTimestamp),
    [startingTimestamp, endingTimestamp]
  )

  return (
    <div className="w-full">
      <div className="relative ml-auto mr-auto flex" style={{ width: PRICE_REVISIONS_CHART_WIDTH }}>
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-0 border-dashed')} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '80%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '60%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '40%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '20%' }} />
        {/*<div className={classNames(CHART_GRID_COMMON_PROPS, 'bottom-0')} />*/}
        <div
          style={{ width: PRICE_REVISIONS_CHART_WIDTH }}
          /**
           * Fix a problem with chart tooltip not hiding correctly on mouse leave
           * See: https://github.com/recharts/recharts/issues/2726
           */
          onMouseEnter={() => {
            onSetIsActive(true)
          }}
          onMouseLeave={() => {
            onSetIsActive(false)
          }}
        >
          <ResponsiveContainer height={PRICE_REVISIONS_CHART_HEIGHT}>
            <ComposedChart
              data={chartData}
              syncId="estimateRevisions"
              margin={{ left: 10, right: 4, top: 0, bottom: 0 }}
              onMouseMove={(props) => {
                if (typeof props.activeTooltipIndex === 'number' && props.activeTooltipIndex !== activeIndex) {
                  onSetActiveIndex(props.activeTooltipIndex)
                }
              }}
              onMouseLeave={() => {
                onSetActiveIndex(-1)
              }}
            >
              <XAxis
                axisLine={{ stroke: getEarningsColor(EarningsColor.axis) }}
                dataKey="timestamp"
                domain={[startingTimestamp, endingTimestamp]}
                dy={2}
                height={28}
                scale="time"
                tick={X_TICK_STYLE}
                tickFormatter={(value: number) => formatMonth(value)}
                tickLine={false}
                ticks={monthlyTicks}
                type="number"
              />
              <YAxis
                axisLine={false}
                domain={['auto', 'auto']}
                interval="preserveStartEnd"
                orientation="left"
                tick={REVISIONS_LEFT_TICK_STYLE}
                tickCount={PRICE_REVISIONS_TICK_COUNT}
                tickFormatter={(value) => shortFormatNumber(value)}
                tickLine={false}
                type="number"
                width={PRICE_REVISIONS_YAXIS_WIDTH}
                yAxisId="estimate"
              />
              <YAxis
                axisLine={false}
                domain={['auto', 'auto']}
                interval="preserveStartEnd"
                orientation="right"
                tick={REVISIONS_RIGHT_TICK_STYLE}
                tickCount={PRICE_REVISIONS_TICK_COUNT}
                tickLine={false}
                type="number"
                width={PRICE_REVISIONS_YAXIS_WIDTH}
                yAxisId="price"
              />
              <Area
                activeDot={false}
                connectNulls
                dataKey="lowHigh"
                dot={false}
                fill={getEarningsColor(EarningsColor.lowHigh)}
                fillOpacity={0.12}
                isAnimationActive={false}
                stroke={getEarningsColor(EarningsColor.lowHigh)}
                type="linear"
                yAxisId="estimate"
              ></Area>
              <Line
                activeDot={false}
                connectNulls={false}
                dataKey="price"
                dot={false}
                isAnimationActive={false}
                stroke={getEarningsColor(EarningsColor.price)}
                type="linear"
                yAxisId="price"
              ></Line>
              <Line
                activeDot={false}
                connectNulls={false}
                dataKey="mean"
                dot={false}
                isAnimationActive={false}
                stroke={getEarningsColor(EarningsColor.estimate)}
                type="linear"
                yAxisId="estimate"
              ></Line>
              <Tooltip
                content={() => (
                  <PriceRevisionTooltip
                    estimateType={estimateType}
                    isActive={isActive}
                    revision={chartData[activeIndex]}
                  />
                )}
                cursor={isActive ? { stroke: getEarningsColor(EarningsColor.cursor) } : false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
